import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "danger" | "ghost";
type Size = "md" | "sm" | "icon";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-primary text-on-primary hover:bg-primary-2",
  secondary: "border border-line bg-surface text-ink hover:bg-surface-2",
  danger: "bg-crit text-white hover:opacity-90",
  ghost: "text-primary hover:bg-surface-2",
};

const SIZES: Record<Size, string> = {
  md: "h-10 px-5 rounded-[8px]",
  sm: "h-8 px-3 rounded-[8px] text-[13px]",
  icon: "h-8 w-8 rounded-[8px] justify-center",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2 font-medium whitespace-nowrap",
        "transition-colors duration-150 ease-out",
        "disabled:pointer-events-none disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
